import React from 'react'
import {
  Typography,
  CircularProgress,
  Box,
  Divider,
  IconButton
} from '@mui/material'
import AIIcon from './AIIcon'
import CollapseIcon from './CollapseIcon'

function EmptySearchStrategy ({
  loading = false,
  onToggle,
  userInfo,
  messages = [], // Add messages prop with default empty array
  sessionName = 'Untitled Session', // Add sessionName prop with default value
  selectedRoleTitle = null // Add selectedRoleTitle prop with default null
}) {
  // Create gradient background style with very subtle glow
  const gradientBackground = {
    background: '#FFFFFF',
    backgroundImage: `
      radial-gradient(circle at 100% 63%, rgba(142, 121, 255, 0.05) 0%, rgba(142, 121, 255, 0) 100%),
      radial-gradient(circle at 50% 0%, rgba(82, 197, 255, 0.05) 0%, rgba(82, 197, 255, 0) 100%),
      radial-gradient(circle at 2% 100%, rgba(57, 255, 20, 0.05) 0%, rgba(57, 255, 20, 0) 100%)
    `,
    border: '1px solid',
    borderImageSlice: 1,
    borderImageSource: `
      linear-gradient(to bottom right,
        rgba(142, 121, 255, 0.9),
        rgba(82, 197, 255, 0.9),
        rgba(57, 255, 20, 0.9)
      )
    `
  }

  // Create gradient text style
  const gradientTextStyle = {
    background:
      'linear-gradient(135deg, #8E79FF 0%, #52C5FF 50%, #39FF14 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent'
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        ...gradientBackground,
        borderRadius: '20px'
      }}
    >
      {/* Sidebar Dragger */}
      <Box
        sx={{
          position: 'absolute',
          left: '-7px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '14px',
          height: '60px',
          bgcolor: '#FFFFFF',
          borderRadius: '4px 0 0 4px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10,
          cursor: 'ew-resize',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #EAECF0',
          borderRight: 'none'
        }}
      >
        <Box
          sx={{
            width: '4px',
            height: '20px',
            bgcolor: '#DCDFEA',
            borderRadius: '20px'
          }}
        />
      </Box>

      {/* Section Header - removed white background */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          borderBottom: '1px solid #EAECF0',
          background: 'transparent'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              bgcolor: '#735BF3',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AIIcon size='small' color='#FFFFFF' />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '16px',
              color: '#101828'
            }}
          >
            Requirements
          </Typography>
        </Box>

        {/* Collapse Button */}
        <IconButton
          size='small'
          onClick={onToggle}
          sx={{
            width: '28px',
            height: '28px',
            padding: '4px',
            // bgcolor: '#FFFFFF',
            // border: '1px solid #EAECF0',
            '&:hover': {
              bgcolor: '#F9FAFB'
            }
          }}
        >
          <CollapseIcon size={18} color='#465366' />
        </IconButton>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          padding: '32px',
          flexGrow: 1,
          overflow: 'auto' // Changed from 'hidden' to 'auto' to enable scrolling
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}
        >
          {messages.length === 0 ? (
            // Initial state - show greeting with updated styling
            <>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 500,
                  fontSize: '20px',
                  color: '#8E79FF',
                  lineHeight: '1.2em'
                }}
              >
                Hello, {userInfo?.name || 'Hannah'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#465366',
                  lineHeight: '1.2em'
                }}
              >
                Start chatting to define your Search
              </Typography>
            </>
          ) : selectedRoleTitle ? (
            // Role title selected - show role title
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '20px',
                color: '#465366'
              }}
            >
              {selectedRoleTitle}
            </Typography>
          ) : (
            // Always show greeting instead of session name
            <>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 500,
                  fontSize: '20px',
                  color: '#8E79FF',
                  lineHeight: '1.2em'
                }}
              >
                Hello, {userInfo?.name || 'Hannah'}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 600,
                  fontSize: '20px',
                  color: '#465366',
                  lineHeight: '1.2em'
                }}
              >
                Start chatting to define your Search
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ borderColor: '#EAECF0' }} />

        {/* Loading or Empty State */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flexGrow: 1,
            gap: '16px',
            padding: '32px 0'
          }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={32}
                sx={{ color: '#735BF3', marginBottom: '16px' }}
              />
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 500,
                  fontSize: '16px',
                  color: '#465366',
                  textAlign: 'center'
                }}
              >
                Refreshing search results...
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#667085',
                  textAlign: 'center'
                }}
              >
                Please wait while we update your search strategy.
              </Typography>
            </>
          ) : (
            <>
              {/* Exact SVG artwork from Figma */}
              <Box
                sx={{
                  position: 'relative',
                  width: '300px',
                  // height: '200px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // marginBottom: '16px'
                }}
              >
                {/* <svg width="300" height="200" viewBox="0 0 392 308" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g opacity="0.6">
                    <rect x="111.288" y="71.5" width="214.5" height="87" rx="9" fill="#DCDFEA"/>
                    <rect x="127.788" y="104.5" width="49.5" height="6" rx="3" fill="#969DAD"/>
                    <rect opacity="0.3" x="127.788" y="119.5" width="181.5" height="6" rx="3" fill="#969DAD"/>
                  </g>
                  <g filter="url(#filter0_dd_468_4577)">
                    <rect x="91.7885" y="89.5" width="216" height="85.5" rx="9" fill="white"/>
                    <rect x="108.288" y="113.5" width="37.5" height="37.5" rx="6" fill="#E1EFFF"/>
                    <path d="M127.038 122.266C132.57 122.266 137.023 126.719 137.023 132.25C137.023 137.781 132.57 142.234 127.038 142.234C121.507 142.234 117.054 137.781 117.054 132.25C117.054 126.719 121.507 122.266 127.038 122.266ZM127.038 126.25C125.117 126.25 123.523 127.797 123.523 129.766C123.523 131.688 125.117 133.234 127.038 133.234C128.96 133.234 130.554 131.688 130.554 129.766C130.554 127.797 128.96 126.25 127.038 126.25ZM127.038 140.266C129.054 140.266 131.492 139.422 133.179 137.359C131.492 136.047 129.335 135.25 127.038 135.25C124.742 135.25 122.585 136.047 120.898 137.359C122.585 139.422 125.023 140.266 127.038 140.266Z" fill="#167FF9"/>
                    <rect x="156.289" y="121.75" width="49.5" height="6" rx="3" fill="#DCDFEA"/>
                    <rect x="156.289" y="136.75" width="135" height="6" rx="3" fill="#EAECF0"/>
                  </g>
                  <circle opacity="0.8" cx="318.25" cy="176.462" r="6.44299" transform="rotate(-34.5885 318.25 176.462)" fill="#4599FA"/>
                  <circle opacity="0.5" cx="309.243" cy="185.455" r="3.20257" transform="rotate(-34.5885 309.243 185.455)" fill="#4599FA"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M76.1661 99.038C72.9191 98.1268 69.5195 97.6671 66.2217 97.246C65.5114 97.1577 64.8519 97.6504 64.8012 98.3444C64.6997 99.039 65.2074 99.6742 65.8669 99.7625C69.0633 100.168 72.3608 100.602 75.4556 101.479C76.1152 101.669 76.8258 101.277 77.0287 100.604C77.2317 99.9304 76.8257 99.2287 76.1661 99.038Z" fill="#4599FA"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M86.0083 86.1192C80.7826 80.7864 74.8973 76.1401 69.773 70.6642C69.3164 70.1528 68.5047 70.1254 67.9973 70.6029C67.49 71.0808 67.4394 71.8844 67.9468 72.3958C73.071 77.8874 78.9563 82.549 84.1821 87.8975C84.6894 88.3967 85.501 88.4038 86.0083 87.9127C86.465 87.4221 86.5157 86.6185 86.0083 86.1192Z" fill="#4599FA"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M95.8014 65.6075C95.9536 68.6597 96.1055 71.7119 96.2578 74.7647C96.2578 75.4638 96.8669 76.0036 97.5772 75.9696C98.2875 75.9351 98.7945 75.34 98.7945 74.6404C98.6423 71.5831 98.4904 68.5263 98.3382 65.4695C98.2875 64.7703 97.6785 64.2341 96.9682 64.2721C96.3087 64.3102 95.7507 64.9088 95.8014 65.6075Z" fill="#4599FA"/>
                  <defs>
                    <filter id="filter0_dd_468_4577" x="85.7885" y="87.5" width="228" height="97.5" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_468_4577"/>
                      <feOffset dy="2"/>
                      <feGaussianBlur stdDeviation="2"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.06 0"/>
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_468_4577"/>
                      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                      <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect2_dropShadow_468_4577"/>
                      <feOffset dy="4"/>
                      <feGaussianBlur stdDeviation="4"/>
                      <feColorMatrix type="matrix" values="0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.1 0"/>
                      <feBlend mode="normal" in2="effect1_dropShadow_468_4577" result="effect2_dropShadow_468_4577"/>
                      <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_468_4577" result="shape"/>
                    </filter>
                  </defs>
                </svg> */}
                <svg
                  width='264'
                  height='126'
                  viewBox='0 0 264 126'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <g opacity='0.6'>
                    <rect
                      x='47.2885'
                      y='7.5'
                      width='214.5'
                      height='87'
                      rx='9'
                      fill='#DCDFEA'
                    />
                    <rect
                      x='63.7885'
                      y='40.5'
                      width='49.5'
                      height='6'
                      rx='3'
                      fill='#969DAD'
                    />
                    <rect
                      opacity='0.3'
                      x='63.7885'
                      y='55.5'
                      width='181.5'
                      height='6'
                      rx='3'
                      fill='#969DAD'
                    />
                  </g>
                  <g filter='url(#filter0_dd_468_4581)'>
                    <rect
                      x='27.7885'
                      y='25.5'
                      width='216'
                      height='85.5'
                      rx='9'
                      fill='white'
                    />
                    <rect
                      x='44.2885'
                      y='49.5'
                      width='37.5'
                      height='37.5'
                      rx='6'
                      fill='#E1EFFF'
                    />
                    <path
                      d='M63.0385 58.2656C68.5697 58.2656 73.0229 62.7188 73.0229 68.25C73.0229 73.7812 68.5697 78.2344 63.0385 78.2344C57.5072 78.2344 53.0541 73.7812 53.0541 68.25C53.0541 62.7188 57.5072 58.2656 63.0385 58.2656ZM63.0385 62.25C61.1166 62.25 59.5229 63.7969 59.5229 65.7656C59.5229 67.6875 61.1166 69.2344 63.0385 69.2344C64.9604 69.2344 66.5541 67.6875 66.5541 65.7656C66.5541 63.7969 64.9604 62.25 63.0385 62.25ZM63.0385 76.2656C65.0541 76.2656 67.4916 75.4219 69.1791 73.3594C67.4916 72.0469 65.3354 71.25 63.0385 71.25C60.7416 71.25 58.5854 72.0469 56.8979 73.3594C58.5854 75.4219 61.0229 76.2656 63.0385 76.2656Z'
                      fill='#167FF9'
                    />
                    <rect
                      x='92.2885'
                      y='57.75'
                      width='49.5'
                      height='6'
                      rx='3'
                      fill='#DCDFEA'
                    />
                    <rect
                      x='92.2885'
                      y='72.75'
                      width='135'
                      height='6'
                      rx='3'
                      fill='#EAECF0'
                    />
                  </g>
                  <circle
                    opacity='0.8'
                    cx='254.25'
                    cy='112.462'
                    r='6.44299'
                    transform='rotate(-34.5885 254.25 112.462)'
                    fill='#4599FA'
                  />
                  <circle
                    opacity='0.5'
                    cx='245.243'
                    cy='121.455'
                    r='3.20257'
                    transform='rotate(-34.5885 245.243 121.455)'
                    fill='#4599FA'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M12.1661 35.038C8.91905 34.1268 5.51949 33.6671 2.2217 33.246C1.5114 33.1577 0.851944 33.6504 0.801209 34.3444C0.699738 35.039 1.20739 35.6742 1.86695 35.7625C5.06327 36.1678 8.36077 36.6016 11.4556 37.4788C12.1152 37.6691 12.8258 37.2774 13.0287 36.6037C13.2317 35.9304 12.8257 35.2287 12.1661 35.038Z'
                    fill='#4599FA'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M22.0083 22.1192C16.7826 16.7864 10.8973 12.1401 5.77305 6.66424C5.31643 6.15283 4.50466 6.12543 3.99731 6.60285C3.48996 7.08078 3.43942 7.88442 3.94677 8.39584C9.07104 13.8874 14.9563 18.549 20.1821 23.8975C20.6894 24.3967 21.501 24.4038 22.0083 23.9127C22.465 23.4221 22.5157 22.6185 22.0083 22.1192Z'
                    fill='#4599FA'
                  />
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M31.8014 1.60746C31.9536 4.6597 32.1055 7.71193 32.2578 10.7647C32.2578 11.4638 32.8669 12.0036 33.5772 11.9696C34.2875 11.9351 34.7945 11.34 34.7945 10.6404C34.6423 7.58307 34.4904 4.52626 34.3382 1.46946C34.2875 0.770329 33.6785 0.234057 32.9682 0.272109C32.3087 0.31016 31.7507 0.908839 31.8014 1.60746Z'
                    fill='#4599FA'
                  />
                  <defs>
                    <filter
                      id='filter0_dd_468_4581'
                      x='21.7885'
                      y='23.5'
                      width='228'
                      height='97.5'
                      filterUnits='userSpaceOnUse'
                      colorInterpolationFilters='sRGB'
                    >
                      <feFlood floodOpacity='0' result='BackgroundImageFix' />
                      <feColorMatrix
                        in='SourceAlpha'
                        type='matrix'
                        values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                        result='hardAlpha'
                      />
                      <feMorphology
                        radius='2'
                        operator='erode'
                        in='SourceAlpha'
                        result='effect1_dropShadow_468_4581'
                      />
                      <feOffset dy='2' />
                      <feGaussianBlur stdDeviation='2' />
                      <feColorMatrix
                        type='matrix'
                        values='0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.06 0'
                      />
                      <feBlend
                        mode='normal'
                        in2='BackgroundImageFix'
                        result='effect1_dropShadow_468_4581'
                      />
                      <feColorMatrix
                        in='SourceAlpha'
                        type='matrix'
                        values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                        result='hardAlpha'
                      />
                      <feMorphology
                        radius='2'
                        operator='erode'
                        in='SourceAlpha'
                        result='effect2_dropShadow_468_4581'
                      />
                      <feOffset dy='4' />
                      <feGaussianBlur stdDeviation='4' />
                      <feColorMatrix
                        type='matrix'
                        values='0 0 0 0 0.0627451 0 0 0 0 0.0941176 0 0 0 0 0.156863 0 0 0 0.1 0'
                      />
                      <feBlend
                        mode='normal'
                        in2='effect1_dropShadow_468_4581'
                        result='effect2_dropShadow_468_4581'
                      />
                      <feBlend
                        mode='normal'
                        in='SourceGraphic'
                        in2='effect2_dropShadow_468_4581'
                        result='shape'
                      />
                    </filter>
                  </defs>
                </svg>
              </Box>

              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '1.171875em',
                  color: '#465366',
                  textAlign: 'center'
                }}
              >
                Continue the conversation to build your search criteria.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EmptySearchStrategy
